'use client';
import React, { useState, useEffect } from 'react';
import { RefreshCw, Plus, CalendarOff } from 'lucide-react';
import AdminKpiBentoGrid from './AdminKpiBentoGrid';
import AdminBookingsTable from './AdminBookingsTable';
import AdminWeeklyCalendar from './AdminWeeklyCalendar';
import AdminTodayTimeline from './AdminTodayTimeline';
import AdminChartsRow from './AdminChartsRow';
import BlockDatesModal from './BlockDatesModal';
import { toast } from 'sonner';

export default function AdminDashboardContent() {
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dateLabel, setDateLabel] = useState('');

  useEffect(() => {
    const now = new Date();
    const label = now?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    setDateLabel(label + ' · St. Louis, MO');
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => {
      setIsRefreshing(false);
      toast?.success('Dashboard refreshed');
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{dateLabel}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95"
            >
              <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={() => setBlockModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95"
            >
              <CalendarOff size={13} />
              Block Dates
            </button>
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg gold-gradient text-primary-foreground text-xs font-bold transition-all duration-150 hover:opacity-90 active:scale-95">
              <Plus size={14} />
              New Booking
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-6 space-y-6 max-w-screen-2xl mx-auto">
        {/* KPI Cards */}
        <AdminKpiBentoGrid key={`kpi-${refreshKey}`} />

        {/* Charts row */}
        <AdminChartsRow key={`charts-${refreshKey}`} />

        {/* Weekly calendar + Today timeline */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <AdminWeeklyCalendar key={`cal-${refreshKey}`} />
          </div>
          <div>
            <AdminTodayTimeline key={`timeline-${refreshKey}`} />
          </div>
        </div>

        {/* Bookings table */}
        <AdminBookingsTable key={`table-${refreshKey}`} />
      </div>

      <BlockDatesModal isOpen={blockModalOpen} onClose={() => setBlockModalOpen(false)} />
    </div>
  );
}